import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import DataTable from 'components/DataTable';
import { handleErrorResponse } from 'utils/general';
import { getData } from 'api/general';
import { useAuth } from 'contexts/auth';
import { withLoading } from 'utils/hoc';
import moment from 'moment';
import MKBox from 'components/MaterialKit/MKBox';
import Button from 'components/Button';
import Papa from 'papaparse';
import DatePicker from 'react-flatpickr';
import { useNavigate } from 'react-router-dom';

const JsonAttributesTableSection = ({
  columns,
  table_name,
  id_field,
  extra_params,
  page_options,
  support_export_csv,
  login_required,
  setLoading,
}) => {
  const { auth, setAuth } = useAuth();
  const [rowsData, setRowsData] = useState(null);
  const [allRowsData, setAllRowsData] = useState(null);
  const [sortBy, setSortBy] = useState('createddate');
  const [sortDirection, setSortDirection] = useState('desc');
  const [pageSize, setPageSize] = useState(page_options?.length ? page_options[0] : 10);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  const navigate = useNavigate();

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const fetchFromApi = useCallback(() => {
    const dataParams = {
      ...extra_params,
      ...(startDate && { 'createddate[gte]': moment(startDate).startOf('day').valueOf() }),
      ...(endDate && { 'createddate[lte]': moment(endDate).endOf('day').valueOf() }),
    };
    setLoading(true);
    return getData(`/${table_name}`, dataParams, 'bearer')
      .then(({ data }) => {
        const formattedData = [];
        data.forEach(((datum) => {
          const res = {};
          if (datum?.createddate) {
            res.createddate = moment(datum.createddate).format('YYYY-MM-DD h:mm A');
          }
          const jsonAttributes = JSON.parse(datum?.json_attributes);
          if (jsonAttributes) {
            columns.forEach((column) => {
              if (column.field in jsonAttributes) {
                res[column.field] = jsonAttributes[column.field];
              }
            });
            formattedData.push(res);
          }
        }));
        if (sortBy) {
          const factor = sortDirection === 'asc' ? 1 : -1;
          if (sortBy.includes('createddate')) {
            // eslint-disable-next-line no-nested-ternary
            formattedData.sort((a, b) => ((moment(a[sortBy]) > moment(b[sortBy])) ? 1 * factor : ((moment(b[sortBy]) > moment(a[sortBy])) ? -1 * factor : 0)));
          } else {
            // eslint-disable-next-line no-nested-ternary
            formattedData.sort((a, b) => ((a[sortBy] > b[sortBy]) ? 1 * factor : ((b[sortBy] > a[sortBy]) ? -1 * factor : 0)));
          }
        }
        setAllRowsData(formattedData);
        setRowsData(formattedData.slice(currentPage * pageSize, (currentPage + 1) * pageSize));
        setTotalRows(formattedData.length);
      })
      .catch((err) => {
        handleErrorResponse(err, setAuth);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [extra_params, startDate, endDate, setLoading, table_name, sortBy, currentPage, pageSize, columns, sortDirection, setAuth]);

  const onClickSort = useCallback((headerName) => {
    setSortBy(headerName);
    setSortDirection((previousSortDirection) => (previousSortDirection === 'asc' ? 'desc' : 'asc'));
  }, []);

  useEffect(() => {
    if (table_name) {
      fetchFromApi();
    }
  }, [table_name, fetchFromApi]);

  const downloadCSV = () => {
    let csv = Papa.unparse(allRowsData);
    csv = `\uFEFF\r\n${csv}`;
    const csvData = new Blob([csv], { type: 'text/csv' });
    const csvURL = URL.createObjectURL(csvData);
    const link = document.createElement('a');
    link.href = csvURL;
    link.download = `log_${moment().format('YYYY-MM-DD h:mm A')}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    if (login_required) {
      if (!auth?.user) {
        navigate('/login');
      }
    }
  }, [auth?.user, login_required, navigate]);

  return (
    <MKBox>
      {support_export_csv && (
        <MKBox display="flex" flexDirection="column" mt="1rem">
          <MKBox display="flex" alignItems="center">
            Start Date
            <DatePicker style={{ marginLeft: '1rem' }} options={{ maxDate: endDate }} onChange={([date]) => setStartDate(date)} />
          </MKBox>
          <MKBox display="flex" alignItems="center">
            End Date
            <DatePicker style={{ marginLeft: '1rem' }} options={{ minDate: startDate }} onChange={([date]) => setEndDate(date)} />
          </MKBox>
          <MKBox>
            <Button style={{ margin: '1rem 0', width: 'auto' }} onClick={downloadCSV}>
              Export CSV
            </Button>
          </MKBox>
        </MKBox>
      )}
      <DataTable
        idField={id_field}
        data={rowsData}
        columns={columns}
        onSort={onClickSort}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        rowsPerPage={pageSize}
        rowsPerPageOptions={page_options}
        totalCount={totalRows}
        onRowsPerPageChange={setPageSize}
      />
    </MKBox> 
  );
};

JsonAttributesTableSection.propTypes = {
  page_options: PropTypes.arrayOf(
    PropTypes.object,
  ).isRequired,
  table_name: PropTypes.string.isRequired,
  id_field: PropTypes.string,
  extra_params: PropTypes.object,
  columns: PropTypes.array.isRequired,
  support_export_csv: PropTypes.bool,
  login_required: PropTypes.bool,
  setLoading: PropTypes.func,
};

export default withLoading(JsonAttributesTableSection);
